import React from 'react';
import isEqual from 'react-fast-compare';

import {ArchiveRecord, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import SchemaFieldSelectorManager from 'models/managers/SchemaFieldSelectorManager';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import SchemaFieldSelectorActions from 'registry/schema-field-selector/SchemaFieldSelectorActions';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {
  SchemaFieldSelector,
  SchemaFieldSelectorOptionForUpdate,
  SchemaFieldSelectorOptionToUpdate,
} from 'schemas/SchemaFieldSelectorConstants';
import ExistingSchemaFieldSelectorSettings from 'settings/registry/ExistingSchemaFieldSelectorSettings';
import SchemaFieldSelectorOptionsSettings, {
  isExistingSchemaFieldSelectorOption,
} from 'settings/registry/SchemaFieldSelectorOptionsSettings';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import CenteredHorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import * as modals from 'util/Modals';
import {InputChangeEvent} from 'util/Types';

class SchemaFieldSelectorSettingsPageConnector extends DataConnector<
  BaseSchemaFieldSelectorSettingsPageProps,
  EnhancedProps
> {
  shouldSubscribeToRedux = false;
  stores = [SchemaFieldSelectorStore];

  enhanceProps(): EnhancedProps {
    const selector = this.props.selectorId
      ? this.schemaFieldSelectorStore().getSelectorById(this.props.selectorId)
      : null;
    const registrySelectors = this.schemaFieldSelectorStore().getOrderedSelectorsForRegistry(
      this.props.registryId
    );
    const selectorOptionToUpdate = this.schemaFieldSelectorStore().getSelectorOptionToUpdate();
    return {
      selector,
      registrySelectors,
      selectorOptionToUpdate,
    };
  }
}

export interface BaseSchemaFieldSelectorSettingsPageProps {
  ownerUrlId: string;
  registryId: string;
  selectorId?: string;
}

interface EnhancedProps {
  selector: SchemaFieldSelector | null;
  registrySelectors: Array<SchemaFieldSelector> | null;
  selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate | null;
}

interface SchemaFieldSelectorSettingsPageState {
  archiveRecord?: ArchiveRecord | null;
  loading: boolean;
  name?: string;
  options: Array<SchemaFieldSelectorOptionForUpdate>;
  registrySelectorNames: Array<string>;
  selectedArchivePurposes: Array<string>;
}

interface QueryingProps {
  isWritable: boolean;
}

type CombinedProps = BaseSchemaFieldSelectorSettingsPageProps & EnhancedProps & QueryingProps;

class SchemaFieldSelectorSettingsPage extends React.Component<
  CombinedProps,
  SchemaFieldSelectorSettingsPageState
> {
  constructor(props: CombinedProps) {
    super(props);
    let name;
    let options: Array<SchemaFieldSelectorOptionForUpdate> = [];
    let archiveRecord;
    SchemaFieldSelectorManager.fetchForRegistry(props.registryId);
    if (props.selectorId) {
      const selector = props.selector;
      if (selector) {
        name = selector.name;
        options = selector.allSchemaFieldSelectorOptions;
        archiveRecord = selector.archiveRecord;
      }
    } else {
      name = '';
      options = [];
    }
    // Default to showing archived options if the selector is archived
    let initialArchivePurposes = [NOT_ARCHIVED_PURPOSE_STRING];
    if (archiveRecord) {
      initialArchivePurposes = [...initialArchivePurposes, archiveRecord.purpose];
    }

    this.state = {
      archiveRecord,
      loading: !this.props.registrySelectors,
      name,
      options,
      registrySelectorNames: (props.registrySelectors ?? []).map((rs) => rs.name),
      selectedArchivePurposes: initialArchivePurposes,
    };
  }

  componentDidMount(): void {
    if (this.state.loading) {
      LoadingScreen.show();
    }
  }

  componentDidUpdate(prevProps: CombinedProps, prevState: SchemaFieldSelectorSettingsPageState): void {
    if (!this.state.loading && prevState.loading) {
      LoadingScreen.hide();
    }
    if (
      !isEqual(this.props.selector, prevProps.selector) ||
      !isEqual(this.props.registrySelectors, prevProps.registrySelectors) ||
      !isEqual(this.props.selectorOptionToUpdate, prevProps.selectorOptionToUpdate)
    ) {
      /**
       * selectorOptionToUpdate exists when the user attempts to modify a selector option used by one or more
       * tags. In this case, open a modal asking the user to confirm their decision.
       */
      const {selectorOptionToUpdate, registrySelectors, selector} = this.props;
      if (selectorOptionToUpdate) {
        this.openConfirmationModal(selectorOptionToUpdate);
      }
      this.setState({
        loading: !this.props.registrySelectors,
        registrySelectorNames: (registrySelectors ?? []).map((rs) => rs.name),
        options: [],
        ...(selector && {
          name: selector.name,
          options: selector.allSchemaFieldSelectorOptions,
          archiveRecord: selector.archiveRecord,
        }),
      });
    }
  }

  private setOptions = (options: Array<SchemaFieldSelectorOptionForUpdate>): void => {
    this.setState({...this.state, options});
  };

  private updateSelector = (
    name: string | null,
    options?: Array<SchemaFieldSelectorOptionForUpdate>,
    selectorOptionToUpdate?: SchemaFieldSelectorOptionToUpdate
  ): void => {
    /**
     * Call the edit selector endpoint with the new name and/or options. selectorOptionToUpdateId is passed in
     * when this method is called when confirming the rename/delete of an option used as a tag value.
     */
    LoadingScreen.show('Saving...');
    SchemaFieldSelectorActions.update(this.props.registryId, this.props.selectorId!, {
      ...(name && {name}),
      schemaFieldSelectorOptions: options,
      selectorOptionToUpdate,
    }).always(() => {
      LoadingScreen.hide();
    });
  };

  private handleChangeSelectedArchivePurposes = (selectedArchivePurposes: Array<string>): void => {
    this.setState({selectedArchivePurposes});
  };

  private isWritable = (): boolean => this.props.isWritable && !this.state.archiveRecord;

  private validateSelectorName = (initialName: string | null, newName: string): boolean => {
    const sanitizedNewName = newName.trim();
    if (initialName !== sanitizedNewName && this.state.registrySelectorNames.includes(sanitizedNewName)) {
      notifications.error('A dropdown with that name already exists in the registry');
      return false;
    }
    return true;
  };

  private validateCreateOptions = (): boolean => {
    // Used when creating a new selector (options added in bulk)
    const optionNames = this.state.options.map((option) => option.name);
    const sanitizedOptionNames = optionNames.map((name) => name.trim());
    if (!sanitizedOptionNames.every((name) => name.length)) {
      notifications.error('Every option must have a name');
      return false;
    }
    if (sanitizedOptionNames.length !== new Set(sanitizedOptionNames).size) {
      notifications.error('You cannot have two options with the same name');
      return false;
    }
    return true;
  };

  private confirmRemoveOptionWithTags = (selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate): void => {
    const newOptions = this.state.options.filter(
      (option) => !isExistingSchemaFieldSelectorOption(option) || option.id !== selectorOptionToUpdate.id
    );
    this.updateSelector(null, newOptions, selectorOptionToUpdate);
  };

  private confirmRenameOptionWithTags = (
    selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate,
    newName: string
  ): void => {
    const newOptions = this.state.options.map((option) =>
      isExistingSchemaFieldSelectorOption(option) && option.id === selectorOptionToUpdate.id
        ? {...option, name: newName.trim()}
        : option
    );
    this.updateSelector(null, newOptions, selectorOptionToUpdate);
  };

  private confirmArchiveOptionWithTags = (
    selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate
  ): void => {
    const newOptions = this.state.options.map((option) =>
      isExistingSchemaFieldSelectorOption(option) && option.id === selectorOptionToUpdate.id
        ? {
            ...option,
            archiveRecord: {
              ...option.archiveRecord,
              purpose: selectorOptionToUpdate.newArchiveRecord!.purpose,
            },
          }
        : option
    );
    this.updateSelector(null, newOptions, selectorOptionToUpdate);
  };

  private handleReturnToRegistry = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getRegistrySelectorsSettingsUrl(this.props.ownerUrlId, this.props.registryId)
    );
  };

  private handleCreateSelector = (): void => {
    if (this.state.name && this.validateSelectorName(null, this.state.name) && this.validateCreateOptions()) {
      LoadingScreen.show('Saving...');
      const sanitizedOptions = this.state.options.map((option: SchemaFieldSelectorOptionForUpdate) => ({
        ...option,
        name: option.name.trim(),
      }));
      SchemaFieldSelectorActions.create({
        // Whitespace should already have been stripped at this point, but doesn't hurt to strip again
        name: this.state.name.trim(),
        schemaFieldSelectorOptions: sanitizedOptions,
        registryId: this.props.registryId,
      })
        .then(() => {
          this.handleReturnToRegistry();
        })
        .always(() => {
          LoadingScreen.hide();
        });
    }
  };

  private openConfirmationModal = (selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate): void => {
    const {numLinkedTags, newName, newArchiveRecord, shouldDelete} = selectorOptionToUpdate;
    const fieldString = BenchlingUtil.pluralize(numLinkedTags, 'field');
    if (shouldDelete) {
      modals
        .confirm(
          'Delete Option',
          `This option is used as a field value in ${numLinkedTags} file ${fieldString}.
        Deleting will reset the values of all fields using this option. Are you sure you want to delete?`,
          'danger'
        )
        .done(() => {
          this.confirmRemoveOptionWithTags(selectorOptionToUpdate);
        })
        .fail(() => {
          SchemaFieldSelectorActions.cancelUpdate();
        });
    } else if (newArchiveRecord) {
      modals
        .confirm(
          'Archive Option',
          `This option is used as a field value in ${numLinkedTags} file ${fieldString}.
           Archiving it will not change the values of fields using this option, but it will hide search filter
           options for it. Are you sure you want to archive?`,
          'danger'
        )
        .done(() => {
          this.confirmArchiveOptionWithTags(selectorOptionToUpdate);
        })
        .fail(() => {
          SchemaFieldSelectorActions.cancelUpdate();
        });
    } else {
      modals
        .confirm(
          'Rename Option',
          `This option is used as a value in ${numLinkedTags} file ${fieldString}. Renaming
        will change the values of all fields using this option to "${newName}". Are you sure you want to
        rename?`,
          'danger'
        )
        .done(() => {
          this.confirmRenameOptionWithTags(selectorOptionToUpdate, newName!);
        })
        .fail(() => {
          SchemaFieldSelectorActions.cancelUpdate();
        });
    }
  };

  render(): JSX.Element {
    if (this.props.selector) {
      BenchlingUtil.setWindowTitle(`${this.props.selector.name} - Dropdowns`);
    }

    if (this.state.loading) {
      return <CenteredHorizontalThrobber />;
    }

    return (
      <div
        className="settingsPage"
        {...getTestProps({component: 'SchemaFieldSelectorSettingsPage', element: 'root'})}
      >
        <div className="settingsPage-contentWrapper">
          <div className="u-lr-split">
            <div>
              <span className="settingsPageContainer-banner" onClick={this.handleReturnToRegistry}>
                Dropdowns /
              </span>

              <div className="legacy-font-proxima-17">
                {this.props.selectorId && (
                  <span>
                    {this.state.name || 'New Dropdown'}
                    <ArchiveRecordLabel
                      item={{
                        id: this.props.selectorId,
                        name: this.props.selector?.name ?? '',
                        archiveRecord: this.state.archiveRecord,
                      }}
                    />
                  </span>
                )}
              </div>
            </div>
            {this.props.selectorId && licenses.hasEnterprisePlan() && (
              <ExportAuditLogButton ids={[this.props.selectorId]} />
            )}
          </div>
          <div className="u-large-spacing-top">
            {/* use this col for spacing */}
            <Col sm={1} />
            <Col sm={11}>
              {this.props.selectorId && this.props.selector ? (
                <ExistingSchemaFieldSelectorSettings
                  isWritable={this.isWritable}
                  selector={this.props.selector}
                  selectorId={this.props.selectorId}
                  validateSelectorName={this.validateSelectorName}
                  options={this.state.options}
                  setOptions={this.setOptions}
                  selectedArchivePurposes={this.state.selectedArchivePurposes}
                  handleChangeSelectedArchivePurposes={this.handleChangeSelectedArchivePurposes}
                  updateSelector={this.updateSelector}
                />
              ) : (
                <div>
                  <div className="settingsPageContainer-section">
                    <div className="metadata-header">Name</div>
                    <div className="selectorSettings-nameInput">
                      <FormInput
                        autoFocus={true}
                        onChange={(e: InputChangeEvent) => {
                          this.setState({name: e.target.value.trim()});
                        }}
                        placeholder="Enter dropdown name"
                        dataTestAttrs={{component: 'SchemaFieldSelectorSettingsPage', element: 'nameInput'}}
                      />
                    </div>
                  </div>
                  <div className="settingsPageContainer-section">
                    <SchemaFieldSelectorOptionsSettings
                      isWritable={this.isWritable}
                      options={this.state.options}
                      setOptions={this.setOptions}
                      selectedArchivePurposes={this.state.selectedArchivePurposes}
                      handleChangeSelectedArchivePurposes={this.handleChangeSelectedArchivePurposes}
                      selectorId={this.props.selectorId}
                      updateSelector={this.updateSelector}
                    />
                    <div className="u-small-spacing-top">
                      {!this.props.selectorId && (
                        <Button
                          buttonStyle={ButtonStyle.PRIMARY}
                          disabled={(this.state.name ?? '').trim().length === 0}
                          onClick={this.handleCreateSelector}
                          size={ButtonSize.MEDIUM}
                          text="Create"
                          dataTestAttrs={{
                            component: 'SchemaFieldSelectorSettingsPage',
                            element: 'createButton',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </div>
        </div>
      </div>
    );
  }
}

function QueryingSchemaFieldSelectorSettingsPage(
  props: BaseSchemaFieldSelectorSettingsPageProps & EnhancedProps
): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  return <SchemaFieldSelectorSettingsPage {...props} isWritable={isWritable} />;
}

export default SchemaFieldSelectorSettingsPageConnector.connect(QueryingSchemaFieldSelectorSettingsPage);
